/**
 * ReportingCharts — dependency-free inline-SVG chart primitives for the
 * Reporting tab, following the same idiom as Oracle's ChartPreview: pure
 * client SVG, viewBox scaling, no chart library. Palette stays inside the
 * module's language: slate ramp for structure, orange for activity,
 * emerald/amber/red for good/warn/bad.
 */
import React from 'react';

const INK = '#64748b';
const AXIS = '#e2e8f0';
const LABEL = '#94a3b8';

/**
 * Grouped vertical bars. groups = [{ label, values: [n per series] }],
 * series = [{ key, label, color }]. Value labels sit above each bar.
 */
export function GroupedBars({ groups, series, height = 150 }) {
  const padT = 14, padB = 18, padL = 4;
  const barW = 12, gap = 4;
  const gw = Math.max(series.length * (barW + gap) + 20, 64);
  const W = padL * 2 + groups.length * gw;
  const H = height;
  const plotH = H - padT - padB;
  const maxV = Math.max(1, ...groups.flatMap(g => g.values.map(v => Number(v) || 0)));
  return (
    <div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-1">
        {series.map(s => (
          <span key={s.key} className="inline-flex items-center gap-1 text-[10px] text-slate-500">
            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: s.color }} />{s.label}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto">
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W * 1.6, minWidth: Math.min(W, 560) }} role="img" aria-label="Grouped bar chart">
          <line x1={0} y1={padT + plotH} x2={W} y2={padT + plotH} stroke={AXIS} />
          {groups.map((g, gi) => (
            <g key={gi} transform={`translate(${padL + gi * gw}, 0)`}>
              {g.values.map((raw, si) => {
                const v = Number(raw) || 0;
                const bh = (v / maxV) * plotH;
                const x = 10 + si * (barW + gap);
                return (
                  <g key={si}>
                    <rect x={x} y={padT + plotH - bh} width={barW} height={Math.max(bh, v ? 2 : 0)} rx="2" fill={series[si].color} />
                    {v > 0 && <text x={x + barW / 2} y={padT + plotH - bh - 3} fontSize="8" textAnchor="middle" fill={INK}>{v}</text>}
                  </g>
                );
              })}
              <text x={gw / 2} y={H - 5} fontSize="9" textAnchor="middle" fill={LABEL}>{g.label}</text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

/** Single-series vertical bars. items = [{ label, value, color }]. */
export function BucketBars({ items, height = 120 }) {
  const padT = 14, padB = 26, padL = 4;
  const gw = 84;
  const W = padL * 2 + items.length * gw;
  const H = height;
  const plotH = H - padT - padB;
  const maxV = Math.max(1, ...items.map(i => Number(i.value) || 0));
  return (
    <div className="overflow-x-auto">
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W * 1.6, minWidth: Math.min(W, 480) }} role="img" aria-label="Bar chart">
        <line x1={0} y1={padT + plotH} x2={W} y2={padT + plotH} stroke={AXIS} />
        {items.map((it, i) => {
          const v = Number(it.value) || 0;
          const bh = (v / maxV) * plotH;
          const x = padL + i * gw + gw / 2;
          return (
            <g key={i}>
              <rect x={x - 11} y={padT + plotH - bh} width={22} height={Math.max(bh, v ? 2 : 0)} rx="2" fill={it.color} />
              <text x={x} y={padT + plotH - bh - 3} fontSize="9" textAnchor="middle" fill={INK}>{v}</text>
              <text x={x} y={H - 14} fontSize="8.5" textAnchor="middle" fill={LABEL}>{String(it.label).slice(0, 20)}</text>
              {it.sublabel && <text x={x} y={H - 4} fontSize="7.5" textAnchor="middle" fill={LABEL}>{String(it.sublabel).slice(0, 22)}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Tiny trend line. points = [numbers]. */
export function Sparkline({ points, width = 180, height = 40, stroke = '#f97316' }) {
  if (!points || !points.length) return null;
  const pad = 4;
  const max = Math.max(...points.map(p => Number(p) || 0), 1);
  const step = points.length > 1 ? (width - pad * 2) / (points.length - 1) : 0;
  const y = v => height - pad - ((Number(v) || 0) / max) * (height - pad * 2);
  const d = points.map((v, i) => `${i ? 'L' : 'M'} ${pad + i * step} ${y(v)}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Trend line">
      <path d={d} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((v, i) => <circle key={i} cx={pad + i * step} cy={y(v)} r="2" fill={stroke} />)}
    </svg>
  );
}

/** Horizontal meter with a percent readout. rate is a 0..1 fraction. */
export function MeterBar({ label, rate, sub, barClass = 'bg-emerald-500' }) {
  const pct = rate === null || rate === undefined ? null : Math.round(Number(rate) * 100);
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-700">{label}</span>
        <span className="font-bold text-slate-800">{pct === null ? 'n/a' : `${pct}%`}</span>
      </div>
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
        <div className={`h-full rounded-full ${barClass}`} style={{ width: `${Math.min(Math.max(pct || 0, 0), 100)}%` }} />
      </div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}
